import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import DataTable from '../../../components/orizon-ui/DataTable';
import SearchInput from '../../../components/orizon-ui/SearchInput';
import StatusPill from '../../../components/orizon-ui/StatusPill';
import ExportMenu from '../../../components/orizon-ui/ExportMenu';
import ImpostazioniTabella, { FiltriBadge } from '../../../components/orizon-ui/ImpostazioniTabella';
import { useToast } from '../../../components/orizon-ui/Toast';
import { useListaColata } from '../../../components/colata/useListaColata';
import { FORMATI_SCANNER } from '../../../lib/colataBarcode';
import { formattaQta, formattaDataOra, formattaData } from '../../../components/gru/scorta';
import { ETICHETTE_MOVIMENTO_COLATA as ETICHETTE_MOVIMENTO } from '../../../components/colata/movimenti';
import { opzioniFiltroImpianti, opzioniFiltroLinee } from '../../../components/colata/opzioniZona';
import { esportaCSV, esportaXLSX, esportaPDF } from '../../../components/gru/esporta';
import { colataOpzioni, colataEsporta, mapColataError } from '../../../lib/services/colataService';
import DettaglioMovimento, { unitaDi } from './DettaglioMovimento';

const MAX_FILTRI = 5;

const COLONNE_VISIBILI = [
  { key: 'created_at', label: 'Data' },
  { key: 'tipo', label: 'Tipo' },
  { key: 'codice', label: 'Codice' },
  { key: 'nome', label: 'Articolo' },
  { key: 'qta_delta', label: 'Movimentato' },
  { key: 'qta_dopo', label: 'Q.tà dopo' },
  { key: 'zona_codice', label: 'Zona' },
  { key: 'impianto_nome', label: 'Impianto' },
  { key: 'causa_label', label: 'Causa' },
  { key: 'utente_nome', label: 'Utente' },
];

const TIPI = Object.entries(ETICHETTE_MOVIMENTO).map(([value, v]) => ({ value, label: v.label }));

const TabellaStorico = () => {
  const toast = useToast();

  const [elencoZone, setElencoZone] = useState([]);
  const [cause, setCause] = useState([]);
  const [elencoImpianti, setElencoImpianti] = useState([]);
  const [elencoLinee, setElencoLinee] = useState([]);

  const [impostazioniAperte, setImpostazioniAperte] = useState(false);
  const [colVisibili, setColVisibili] = useState({});
  const [densita, setDensita] = useState('normale');
  const [filtriUi, setFiltriUi] = useState([]);
  const [esportando, setEsportando] = useState(false);
  const [selezionato, setSelezionato] = useState(null);

  const definizioniFiltri = useMemo(() => [
    {
      key: 'data', label: 'Data', tipo: 'data',
      db: (v) => {
        const [da, a] = String(v).split('|');
        return ['created_at', { da: da || undefined, a: a || undefined }];
      },
    },
    { key: 'tipo', label: 'Tipo di movimento', tipo: 'select', opzioni: TIPI, db: (v) => ['tipo', v] },
    { key: 'zona', label: 'Zona', tipo: 'select', opzioni: elencoZone.map((g) => ({ value: g.id, label: `${g.codice} — ${g.nome}` })), db: (v) => ['zona_id', v] },
    { key: 'impianto', label: 'Impianto', tipo: 'select', opzioni: opzioniFiltroImpianti(elencoImpianti, elencoZone), db: (v) => ['impianto_id', v] },
    { key: 'linea', label: 'Linea', tipo: 'select', opzioni: opzioniFiltroLinee(elencoLinee, elencoZone), db: (v) => ['linea_id', v] },
    { key: 'causa', label: 'Causa', tipo: 'select', opzioni: cause.map((c) => ({ value: c.id, label: c.label })), db: (v) => ['causa_id', v] },
    {
      key: 'stornato', label: 'Stornato', tipo: 'select',
      opzioni: [{ value: 'true', label: 'Sì' }, { value: 'false', label: 'No' }],
      db: (v) => ['stornato', v],
    },
    { key: 'articolo', label: 'Articolo', tipo: 'testo', db: (v) => ['nome', { ilike: v }] },
    { key: 'codice', label: 'Codice', tipo: 'testo', db: (v) => ['codice', { ilike: v }] },
    { key: 'utente', label: 'Utente', tipo: 'testo', db: (v) => ['utente_nome', { ilike: v }] },
  ], [elencoZone, cause, elencoImpianti, elencoLinee]);

  const filtriAttivi = useMemo(() => filtriUi.filter((f) => f.col && f.val), [filtriUi]);

  const filtri = useMemo(() => {
    const risultato = {};
    filtriAttivi.forEach((f) => {
      const def = definizioniFiltri.find((d) => d.key === f.col);
      if (!def) return;
      const [colonna, valore] = def.db(f.val);
      risultato[colonna] = valore;
    });
    return risultato;
  }, [filtriAttivi, definizioniFiltri]);

  const lista = useListaColata({
    entita: 'storico',
    filtri,
    ordineIniziale: { key: 'id', direction: 'desc' },
    perPagina: 25,
  });

  useEffect(() => {
    let annullato = false;
    Promise.all([colataOpzioni('zone'), colataOpzioni('cause'), colataOpzioni('impianti'), colataOpzioni('linee')])
      .then(([g, c, imp, lin]) => {
        if (annullato) return;
        setElencoZone(g);
        setCause(c);
        setElencoImpianti(imp);
        setElencoLinee(lin);
      })
      .catch((e) => {
        if (!annullato) toast.error(mapColataError(e).message);
      });
    return () => { annullato = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Esportazione ──────────────────────────────────────────────────────────
  const COLONNE_EXPORT = [
    { label: 'Data', valore: (r) => formattaDataOra(r.created_at), larghezza: 18, align: 'left' },
    { label: 'Tipo', valore: (r) => ETICHETTE_MOVIMENTO[r.tipo]?.label ?? r.tipo, larghezza: 14, align: 'center' },
    { label: 'Codice', valore: (r) => r.codice, larghezza: 16, align: 'left' },
    { label: 'Articolo', valore: (r) => r.nome, larghezza: 38, align: 'left' },
    {
      label: 'Movimentato', valore: (r) => (r.tipo === 'ordine' ? Number(r.intervento_qta) : Number(r.qta_delta)), larghezza: 12, align: 'center',
      colore: (r) => (r.tipo === 'ordine' ? [100, 116, 139] : Number(r.qta_delta) > 0 ? [16, 185, 129] : [239, 68, 68]),
    },
    { label: 'Q.tà prima', valore: (r) => Number(r.qta_prima), larghezza: 11, align: 'center' },
    { label: 'Q.tà dopo', valore: (r) => Number(r.qta_dopo), larghezza: 11, align: 'center' },
    { label: 'Unità', valore: (r) => unitaDi(r), larghezza: 8, align: 'center' },
    { label: 'Zona', valore: (r) => r.zona_codice ?? '', larghezza: 12, align: 'center' },
    { label: 'Impianto', valore: (r) => r.impianto_nome ?? '', larghezza: 22, align: 'left' },
    { label: 'Linea', valore: (r) => r.linea_nome ?? '', larghezza: 10, align: 'center' },
    { label: 'Causa', valore: (r) => r.causa_label ?? '', larghezza: 20, align: 'left' },
    { label: 'Utente', valore: (r) => r.utente_nome, larghezza: 20, align: 'left' },
    { label: 'Stornato', valore: (r) => (r.stornato ? 'Sì' : ''), larghezza: 10, align: 'center' },
    { label: 'Nota', valore: (r) => r.motivo ?? '', larghezza: 40, align: 'left' },
  ];

  const descrizioneFiltri = () => {
    const parti = [];
    if (lista.ricercaInput.trim()) parti.push(`Ricerca: "${lista.ricercaInput.trim()}"`);
    filtriAttivi.forEach((f) => {
      const def = definizioniFiltri.find((d) => d.key === f.col);
      let valore = f.val;
      if (def?.tipo === 'select') valore = def.opzioni.find((o) => o.value === f.val)?.label ?? f.val;
      if (def?.tipo === 'data') {
        const [da, a] = String(f.val).split('|');
        valore = `${da ? formattaData(da) : '…'} → ${a ? formattaData(a) : '…'}`;
      }
      parti.push(`${def?.label ?? f.col}: ${valore}`);
    });
    return parti.length ? `Filtri — ${parti.join(' · ')}` : '';
  };

  const esporta = async (formato) => {
    setEsportando(true);
    try {
      const { righe, totale, troncato } = await colataEsporta('storico', {
        sort: lista.sort,
        ricerca: lista.ricercaInput,
        filtri,
      });
      if (righe.length === 0) {
        toast.warn('Nessun movimento da esportare.');
        return;
      }
      const base = { nomeFile: 'storico_colata', colonne: COLONNE_EXPORT, righe };
      if (formato === 'csv') esportaCSV(base);
      else if (formato === 'xlsx') await esportaXLSX({ ...base, nomeFoglio: 'Storico Colata' });
      else await esportaPDF({ ...base, nomeFile: 'Storico_Colata', titolo: 'STORICO MOVIMENTI MAGAZZINO COLATA', sottotitolo: descrizioneFiltri() });
      toast.success(`Esportati ${righe.length} movimenti.`);
      if (troncato) toast.warn(`Esportazione limitata ai primi ${righe.length} su ${totale} movimenti: restringi i filtri.`);
    } catch (e) {
      toast.error(mapColataError(e).message);
    } finally {
      setEsportando(false);
    }
  };

  // ─── Colonne ───────────────────────────────────────────────────────────────
  const tutteLeColonne = [
    {
      key: 'created_at', label: 'Data', width: 150,
      render: (r) => <span className="font-mono text-[12px] font-bold text-muted-foreground">{formattaDataOra(r.created_at)}</span>,
    },
    {
      key: 'tipo', label: 'Tipo', width: 150, align: 'center',
      render: (r) => {
        const et = ETICHETTE_MOVIMENTO[r.tipo] ?? { label: r.tipo, tono: 'neutro' };
        return (
          <div className="flex flex-col items-center gap-1">
            <StatusPill tono={et.tono} label={et.label} />
            {r.stornato && <StatusPill tono="warn" label="Stornato" />}
          </div>
        );
      },
    },
    {
      key: 'codice', label: 'Codice', width: 150,
      render: (r) => <span className="font-mono text-[12px] font-bold text-muted-foreground/60 group-hover:text-primary/70 transition-colors">{r.codice}</span>,
    },
    {
      key: 'nome', label: 'Articolo', width: 270,
      render: (r) => <span className="text-[13px] font-black uppercase tracking-tight text-foreground/75 group-hover:text-foreground transition-colors leading-tight">{r.nome}</span>,
    },
    {
      key: 'qta_delta', label: 'Movimentato', width: 130, align: 'center',
      render: (r) => {
        if (r.tipo === 'ordine') {
          // l'ordine non muove la giacenza: si mostra la quantità ordinata
          return (
            <div className="flex flex-col items-center gap-0.5">
              <span className="text-[14px] font-black tabular-nums text-foreground/70">
                {formattaQta(r.intervento_qta, 3)}{' '}
                <span className="text-[9px] uppercase tracking-widest text-muted-foreground/40">{unitaDi(r)}</span>
              </span>
              <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/50">ordinati</span>
            </div>
          );
        }
        const positivo = Number(r.qta_delta) > 0;
        return (
          <span className={`text-[15px] font-black tabular-nums ${positivo ? 'text-emerald-500' : 'text-red-500'}`}>
            {positivo ? '+' : ''}{formattaQta(r.qta_delta, 3)}{' '}
            <span className="text-[9px] uppercase tracking-widest text-muted-foreground/40">{unitaDi(r)}</span>
          </span>
        );
      },
    },
    {
      key: 'qta_dopo', label: 'Q.tà dopo', width: 110, align: 'center',
      render: (r) => <span className="text-[13px] font-bold tabular-nums text-foreground/70">{formattaQta(r.qta_dopo, 3)}</span>,
    },
    {
      key: 'zona_codice', label: 'Zona', width: 110, align: 'center',
      render: (r) => (r.zona_codice
        ? <span className="text-[12px] font-mono font-bold text-primary/80">{r.zona_codice}</span>
        : <span className="text-[11px] font-bold text-muted-foreground/30">—</span>),
    },
    {
      key: 'impianto_nome', label: 'Impianto', width: 170,
      render: (r) => (r.impianto_nome
        ? (
          <div className="flex flex-col gap-0.5">
            <span className="text-[12px] font-black uppercase tracking-tight text-foreground/70">{r.impianto_nome}</span>
            {r.linea_nome && <span className="text-[10px] font-bold text-muted-foreground/60">Linea {r.linea_nome}</span>}
          </div>
        )
        : <span className="text-[11px] font-bold text-muted-foreground/30">—</span>),
    },
    {
      key: 'causa_label', label: 'Causa', width: 160,
      render: (r) => <span className="text-[12px] font-bold text-muted-foreground">{r.causa_label ?? '---'}</span>,
    },
    {
      key: 'utente_nome', label: 'Utente', width: 160,
      render: (r) => <span className="text-[11px] font-black uppercase tracking-widest text-muted-foreground/60">{r.utente_nome}</span>,
    },
    {
      key: '__apri', label: '', width: 120, align: 'center', noSort: true,
      render: (r) => (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setSelezionato(r); }}
          className="inline-flex items-center justify-center gap-1.5 w-10 h-10 md:w-auto md:px-5 md:py-2 rounded-2xl bg-primary/5 border border-primary/20 text-primary/60 hover:bg-primary hover:text-primary-foreground hover:border-primary hover:scale-105 active:scale-95 transition-all duration-300 text-[10px] font-black uppercase tracking-[.15em]"
        >
          <Icon name="Eye" size={15} strokeWidth={3} />
          <span className="hidden md:inline">Apri</span>
        </button>
      ),
    },
  ];
  const colonne = tutteLeColonne.filter((c) => c.key === '__apri' || colVisibili[c.key] !== false);

  const rimuoviFiltro = (indice) => setFiltriUi((prec) => prec.filter((_, i) => i !== indice));

  return (
    <div className="w-full animate-in fade-in duration-700">
      <div className="mb-6 px-1">
        <h2 className="text-4xl md:text-5xl font-black tracking-[-0.06em] uppercase leading-none text-foreground">
          STORICO<span className="text-primary">COLATA</span>
        </h2>
        <div className="flex items-center gap-2 mt-2 md:mt-3">
          <span className="h-[2px] w-6 bg-primary rounded-full" />
          <p className="text-[10px] font-black uppercase tracking-[.35em] text-muted-foreground/50">
            Registro dei movimenti di magazzino
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-4">
        <SearchInput value={lista.ricercaInput} onChange={lista.setRicercaInput} placeholder="Cerca articolo, codice, utente..." onScansiona={lista.setRicercaInput} formatiScanner={FORMATI_SCANNER} />

        <ExportMenu onEsporta={esporta} inCorso={esportando} disabilitato={lista.loading} />

        <button
          type="button"
          onClick={() => setImpostazioniAperte(true)}
          className={`relative flex items-center gap-2 px-3 md:px-4 py-2.5 border rounded-2xl text-[11px] font-black uppercase tracking-widest transition-all duration-200 shadow-sm ${
            filtriAttivi.length > 0
              ? 'bg-primary/10 border-primary/30 text-primary hover:bg-primary/20'
              : 'bg-muted/40 hover:bg-muted/80 border-border/40 hover:border-primary/30 text-muted-foreground hover:text-foreground'
          }`}
        >
          <Icon name={filtriAttivi.length > 0 ? 'Filter' : 'Settings2'} size={14} />
          <span className="hidden sm:inline">{filtriAttivi.length > 0 ? `Filtri (${filtriAttivi.length})` : 'Impostazioni'}</span>
          {filtriAttivi.length > 0 && (
            <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-primary text-primary-foreground text-[9px] font-black flex items-center justify-center">
              {filtriAttivi.length}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={lista.ricarica}
          disabled={lista.loading}
          className="flex items-center gap-2 px-3 md:px-4 py-2.5 bg-primary/10 hover:bg-primary/20 border border-primary/30 rounded-2xl text-[11px] font-black uppercase tracking-widest text-primary transition-all duration-200 shadow-sm disabled:opacity-50"
        >
          <Icon name="RefreshCw" size={14} className={lista.loading ? 'animate-spin' : ''} />
          <span className="hidden sm:inline">Aggiorna</span>
        </button>
      </div>

      {filtriAttivi.length > 0 && (
        <div className="mb-3">
          <FiltriBadge filtri={filtriUi} definizioni={definizioniFiltri} onRimuovi={rimuoviFiltro} />
        </div>
      )}

      <DataTable
        colonne={colonne}
        righe={lista.righe}
        totale={lista.totale}
        loading={lista.loading}
        pagina={lista.pagina}
        perPagina={lista.perPagina}
        onPagina={lista.setPagina}
        sort={lista.sort}
        onSort={lista.onSort}
        densita={densita}
        onRigaClick={setSelezionato}
        rigaDisattivata={(r) => r.stornato === true}
        messaggioVuoto={lista.ricercaInput || filtriAttivi.length ? 'Nessun movimento corrisponde alla ricerca' : 'Nessun movimento registrato'}
        altezzaMax="max-h-[65dvh]"
      />

      {impostazioniAperte && (
        <ImpostazioniTabella
          colonne={COLONNE_VISIBILI}
          colVisibili={colVisibili}
          setColVisibili={setColVisibili}
          densita={densita}
          setDensita={setDensita}
          filtri={filtriUi}
          setFiltri={setFiltriUi}
          definizioniFiltri={definizioniFiltri}
          maxFiltri={MAX_FILTRI}
          onClose={() => setImpostazioniAperte(false)}
        />
      )}

      {selezionato && (
        <DettaglioMovimento
          movimento={selezionato}
          onClose={() => setSelezionato(null)}
          onAggiornato={lista.ricarica}
        />
      )}
    </div>
  );
};

export default TabellaStorico;
